import type { FormikContextType } from 'formik';
import { getIn } from 'formik';
import { Duration } from 'luxon';
import type { MutableRefObject } from 'react';
import type { PartialDeep, SetNonNullable } from 'type-fest';

import { getTenantUnits } from '@core/configuration';
import { EmptyTenantDefaultMargins, TenantDefaultMargins } from '@core/types/tenantDefaultMargins';

import { NewDeliveryCostOrNullFromFormik, type PlantFormikType } from '../formik/plant';
import type { ProjectFormikType } from '../formik/project';
import {
  AdditionalTruckingInfoEditableFieldsFormikType,
  AdditionalTruckingInfoFormikType,
  FormikProductReference,
  WithDeliveryDisplayOnlyType,
  type ProjectOrQuoteProductFormikType,
} from '../formik/projectOrQuoteProduct';
import { NewQuoteProductFromFormik, type QuoteFormikType } from '../formik/quote';
import { Cost } from '../types/api/Cost/Cost';
import { Currency, NewCurrency } from '../types/api/Currency/Currency';
import { DeliveryCost, NewDeliveryCost } from '../types/api/DeliveryCost/DeliveryCost';
import Enums from '../types/api/Enums';
import type { FixedPricingByCompany } from '../types/api/FixedPricingByCompany/FixedPricingByCompany';
import { Product } from '../types/api/Product/Product';
import type { Project } from '../types/api/Project/Project';
import type { ProjectProduct } from '../types/api/ProjectProduct/ProjectProduct';
import { NewQuoteProduct, QuoteProduct } from '../types/api/QuoteProduct/QuoteProduct';
import { CalculateTargetMarginCurrency } from '../utils/costCalculations';
import { NullableCostString, NullableCurrencyString } from '../utils/currency';
import { adjustDuration } from '../utils/dateHelpers';
import { cleanedDecimal, formatDecimalString } from '../utils/decimals';
import {
  ExceedingDiscounts,
  pendingDiscounts,
  repricedAtGross,
  rescaledToGrossPrice,
  rescaledToNetPrice,
  savedPercentSharesOfRows,
  settledNetPrice,
} from '../utils/discounts';
import { hasStringValue, hasValue } from '../utils/equality';
import { resolveCurrencyCode } from '../utils/format';
import {
  FormikCurrency,
  FormikDeliveryCosts,
  FormikNullableCurrency,
} from '../utils/formikHelpers';
import { AppliedTierType, calculateTierAdjustment } from '../utils/tierPricing';
import { CalculateWithDeliveryTotalPrice } from '../utils/withDeliveryPriceCalculations';
import type { YupSchemaDeliveryCostsType, YupSchemaReferenceType } from '../utils/yupHelpers';

/**
 * SharedFormikType is a combination of possible formik states based on where
 * the ProductSection might be used. When used in the Project drawer, it will
 * rely on the ProjectFormikType. When used in the Quote drawer, it will rely
 * on the QuoteFormikType. This type is used to ensure that the ProductSection
 * can be used in both contexts.
 */
export type SharedFormikType = Pick<ProjectFormikType, 'productsByPlants' | 'hasDistanceChanged'> &
  Partial<
    Pick<QuoteFormikType, 'mainProductsByPlants' | 'additionalProductsByPlants' | 'company'>
  > & {
    id?: string;
    plantDistance?: string | null;
    plantDistanceUnit?: Enums.Unit | null;
    plantDriveTime?: string | null;
  };
export type ProductSectionType =
  | `productsByPlants.${number}.products`
  | `mainProductsByPlants.${number}.products`
  | `additionalProductsByPlants.${number}.products`;

export type ProductFieldPathPrefix =
  | `productsByPlants.${number}.products.${number}`
  | `mainProductsByPlants.${number}.products.${number}`
  | `additionalProductsByPlants.${number}.products.${number}`;

export type AdditionalTruckingInfoFieldPathPrefix =
  | `productsByPlants.${number}.products.${number}.additionalTruckingInfos.${number}`
  | `mainProductsByPlants.${number}.products.${number}.additionalTruckingInfos.${number}`
  | `additionalProductsByPlants.${number}.products.${number}.additionalTruckingInfos.${number}`;

export type TruckingTypeDefaults = {
  haulRate: Currency | null;
  haulCost: Currency | null;
  minimumHaulCharge: number | null;
};

export type TruckingTypeActions = {
  onHaulRateChange: (numberStr: string) => void;
  onMinimumHaulChargeChange: (value: number | null) => void;
  onHaulTaxableChange: (haulTaxable: boolean) => void;
  onTaxRateChange: (taxRateStr: string) => void;
  onHaulCostChange: (numberStr: string) => void;
  onTruckingTypeChange: (
    defaults: TruckingTypeDefaults,
    truckingType?: YupSchemaReferenceType,
  ) => void;
};

type FormikProductsByPlantsLike = {
  products: {
    product: {
      id: string | null;
    };
  }[];
}[];

export const getFilteredProductsRefsByFormikProducts = ({
  curProductsRef,
  formikProductsByPlants,
}: {
  curProductsRef: Record<string, Product>;
  formikProductsByPlants: FormikProductsByPlantsLike;
}): Record<string, Product | null> => {
  const filteredProductsRefs: Record<string, Product | null> = {};

  formikProductsByPlants.forEach((plant) => {
    plant.products.forEach((formikProduct) => {
      const productId = formikProduct.product.id;
      if (!hasValue(productId)) return;
      filteredProductsRefs[productId] = curProductsRef[productId] ?? null;
    });
  });

  return filteredProductsRefs;
};

export const resolveDefaultCurrencyCode = ({
  plantProduct,
}: {
  plantProduct?: Product | null;
}): string => {
  const normalizedPlantCurrencyCode = plantProduct?.plant.currencyCode?.trim();
  if (hasStringValue(normalizedPlantCurrencyCode)) {
    return normalizedPlantCurrencyCode;
  }

  const tenantUnits = getTenantUnits();
  return tenantUnits.currencyCode;
};

export type ProductCurrencyResync = {
  groupIndex: number;
  productIndex: number;
  currencyCode: string;
};

/**
 * Returns the formik product rows, across all of a section's location groups,
 * whose stamped price currency disagrees with their group's selected plant
 * currency. Price currencies are stamped from resolveDefaultCurrencyCode at
 * edit/prefill time, which falls back to the tenant default while plant data
 * is not yet loaded (and project-product prefill never hydrates the nested
 * plant at all), so a non-USD plant's product can be left carrying the wrong
 * code. The server rejects that as a currency mismatch, so callers re-stamp
 * these rows from the group's plant once the plants list is available.
 */
export const staleGroupProductCurrencyResyncs = (
  groups: Array<{
    plant: { id: string | null };
    products: Array<{
      price: { currency: string };
      originalPrice?: { currency: string } | null;
    }>;
  }>,
  getPlantCurrencyCode: (plantId: string) => string | null,
): ProductCurrencyResync[] => {
  const resyncs: ProductCurrencyResync[] = [];
  groups.forEach((group, groupIndex) => {
    const plantId = group.plant.id;
    if (!hasStringValue(plantId)) {
      return;
    }

    // Only re-stamp from a loaded plant with a real currency code; the
    // fallbacks are what produced the stale stamp in the first place.
    const plantCurrencyCode = getPlantCurrencyCode(plantId)?.trim();
    if (!hasStringValue(plantCurrencyCode)) {
      return;
    }

    group.products.forEach((formikProduct, productIndex) => {
      if (
        formikProduct.price.currency !== plantCurrencyCode ||
        (formikProduct.originalPrice && formikProduct.originalPrice.currency !== plantCurrencyCode)
      ) {
        resyncs.push({ groupIndex, productIndex, currencyCode: plantCurrencyCode });
      }
    });
  });

  return resyncs;
};

/**
 * When a new product is selected, conditionally pick the correct delivery cost to use.
 *
 * If there is a project product with an ID matching the selected ID, AND it has a valid
 * delivery cost, then use that.
 *
 * Otherwise, fall back to the current plant delivery costs.
 */
export const newProductDeliveryCost = ({
  projectProduct,
  plantProduct,
}: {
  projectProduct: ProjectProduct | undefined;
  plantProduct: Product;
}): DeliveryCost | null => {
  if (projectProduct !== undefined && projectProduct.deliveryCosts.perMinCost.number !== '0.00') {
    return projectProduct.deliveryCosts;
  }

  return plantProduct.plant.deliveryCosts;
};

export type CascadedDeliveryCostReturn =
  | {
      formikDeliveryCost: QuoteFormikType['mainProductsByPlants'][number]['products'][number]['deliveryCosts'];
      shouldUpdate: false;
    }
  | { deliveryCost: DeliveryCost | null; shouldUpdate: true };

/**
 * Cascade down to use the appropriate delivery costs for calculations.
 *
 * 1. The snapshot QuoteProduct delivery costs are valid
 * 2. The above is not valid, but there is a matching ProjectProduct with
 *    valid delivery costs
 * 3. There is either no ProjectProduct, or it has invalid snapshot delivery
 *    costs, so use the plant delivery costs.
 */
export const CascadedDeliveryCost = ({
  formikProduct,
  projectProduct,
  plantProduct,
}: {
  formikProduct: QuoteFormikType['mainProductsByPlants'][number]['products'][number];
  projectProduct: ProjectProduct | undefined;
  plantProduct: Product;
}): CascadedDeliveryCostReturn => {
  if (
    formikProduct.deliveryCosts.perMinCost !== null &&
    formikProduct.deliveryCosts.perMinCost.number !== '0.00' &&
    formikProduct.deliveryCosts.perMinCost.number !== '0' &&
    formikProduct.deliveryCosts.perMinCost.number !== '0.0000' &&
    parseFloat(formikProduct.deliveryCosts.perMinCost.number ?? '0') !== 0
  ) {
    return {
      formikDeliveryCost: formikProduct.deliveryCosts,
      shouldUpdate: false,
    };
  }

  if (
    projectProduct !== undefined &&
    projectProduct.deliveryCosts.perMinCost.number !== Cost.zero().amount.number
  ) {
    return {
      deliveryCost: projectProduct.deliveryCosts,
      shouldUpdate: true,
    };
  }

  return {
    deliveryCost: plantProduct.plant.deliveryCosts,
    shouldUpdate: true,
  };
};

type BuildInitialDeliveryCostsOptions = {
  deliveryCosts: DeliveryCost | undefined | null;
  drivingTime: string | null;
  drivingDistance: string | null;
};

/** Add driving time and distance to the specified delivery costs. */
export const buildInitialDeliveryCosts = ({
  deliveryCosts,
  drivingTime,
  drivingDistance,
}: BuildInitialDeliveryCostsOptions): DeliveryCost => {
  const parsedDrivingTime = drivingTime ? Duration.fromISO(drivingTime) : Duration.fromObject({});
  const newDC = NewDeliveryCost({
    ...deliveryCosts,
    driveDistanceToJob: drivingDistance ?? '0',
    driveDistanceToPlant: drivingDistance ?? '0',
    driveToJobTime: parsedDrivingTime,
    driveToPlantTime: parsedDrivingTime,
  });

  // It does not automatically calculate totalDeliveryCosts properly.
  const dc = NewDeliveryCost({
    ...newDC,
    totalDeliveryCost: newDC.calculateTotalDeliveryCost() ?? undefined,
  });
  return dc;
};

/** Convert a domain-model DeliveryCost object to its Formik representation. */
export const deliveryCostToFormik = (
  deliveryCosts: DeliveryCost,
  currencyCode?: string | null,
): SetNonNullable<YupSchemaDeliveryCostsType> =>
  FormikDeliveryCosts(
    {
      ...deliveryCosts,
      totalDeliveryCost: deliveryCosts.calculateTotalDeliveryCost() ?? Cost.zero(),
    },
    currencyCode,
  );

/* -------------------------------------------------------------------------- */
/* Display-only recomputation                                                 */
/* -------------------------------------------------------------------------- */

/** Recalculate the value for ProjectOrQuoteProductFormikType.deliveryCosts.displayOnly.totalDeliveryCost */
export const RecalculateTotalDeliveryCost = (deliveryCosts: DeliveryCost | null): string =>
  NullableCostString({ cost: deliveryCosts?.calculateTotalDeliveryCost() ?? null });

/** Recalculate the values for ProjectOrQuoteProductFormikType.displayOnly */
export const RecalculateDisplayOnly = (
  quoteProduct: QuoteProduct,
  marginConfiguration: Enums.MarginConfiguration,
  defaultMargins: TenantDefaultMargins,
): ProjectOrQuoteProductFormikType['displayOnly'] => {
  // Match the currency of backend-loaded products.
  const currencyCode = resolveCurrencyCode(quoteProduct.product.plant.currencyCode);

  const suggestedPriceCurrency = quoteProduct.calculateSuggestedPrice(
    marginConfiguration,
    defaultMargins,
  );
  const suggestedPrice = NullableCurrencyString({
    cur: suggestedPriceCurrency,
    omitNull: false,
    currencyCode,
  });
  const listPrice = NullableCurrencyString({ cur: quoteProduct.product.listPrice, currencyCode });
  const unitCost = quoteProduct.costPerUnitDisplay(currencyCode);

  // Conditionally format target margin based on target margin display configuration
  const targetMargin = quoteProduct.product.targetMarginDisplay(
    marginConfiguration,
    defaultMargins,
  );

  // Always provide both currency and percentage formats for target margin
  const targetMarginCurrency = CalculateTargetMarginCurrency({
    product: quoteProduct.product,
    totalCost: quoteProduct.costPerUnit().toUnit(),
    defaultMargins,
    currencyCode,
  });
  const targetMarginPercentage = quoteProduct.product.targetMarginDisplay(
    Enums.MarginConfiguration.Percentage,
    defaultMargins,
  );

  const actualMarginPercentage = quoteProduct.marginAsPercentage();
  const actualMarginCurrency = quoteProduct.marginAsFormattedCurrency(currencyCode);
  const marginOverMaterialsCurrency =
    quoteProduct.marginOverMaterialAsFormattedCurrency(currencyCode);
  const marginOverMaterialsPercentage = quoteProduct.marginOverMaterialAsPercentage();

  return {
    suggestedPrice,
    listPrice,
    unitCost,
    targetMargin,
    targetMarginPercentage,
    targetMarginCurrency,
    actualMarginPercentage,
    actualMarginCurrency,
    marginOverMaterialsCurrency,
    marginOverMaterialsPercentage,
  };
};

type setRecalculatedRMCostsReturn = {
  totalDeliveryCost: string;
  displayOnly: ReturnType<typeof RecalculateDisplayOnly>;
};

export const setRecalculatedRMCostsNoBag = ({
  deliveryCostInputOverrides = {},
  quoteProductOverrides = {},
  formikProduct,
  plantProduct,
  marginConfiguration,
  defaultMargins,
}: {
  deliveryCostInputOverrides?: PartialDeep<PlantFormikType['deliveryCosts']>;
  quoteProductOverrides?: PartialDeep<QuoteProduct>;
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;
  plantProduct: Product | undefined;
  marginConfiguration: Enums.MarginConfiguration;
  defaultMargins: TenantDefaultMargins;
}): ProjectOrQuoteProductFormikType => {
  // Preserve drive times and distances from formik state if not explicitly overridden
  // This ensures that when products are added to a quote, the calculated drive times
  // from the quote/project location are not lost during recalculation.
  const preservedDeliveryCostInputs = {
    driveToJobTime: formikProduct.deliveryCosts.driveToJobTime,
    driveToPlantTime: formikProduct.deliveryCosts.driveToPlantTime,
    driveDistanceToJob: formikProduct.deliveryCosts.driveDistanceToJob,
    driveDistanceToPlant: formikProduct.deliveryCosts.driveDistanceToPlant,
    ...deliveryCostInputOverrides, // explicit overrides take precedence
  };

  const deliveryCosts =
    NewDeliveryCostOrNullFromFormik(formikProduct.deliveryCosts, preservedDeliveryCostInputs) ??
    DeliveryCost.zero();

  const quoteProductInitial = NewQuoteProductFromFormik(formikProduct, {
    product: plantProduct,
  });
  const quoteProduct = NewQuoteProduct({
    ...quoteProductInitial,
    ...quoteProductOverrides,
    deliveryCosts: {
      ...deliveryCosts,
      totalDeliveryCost: deliveryCosts.calculateTotalDeliveryCost() ?? Cost.zero(),
    },
  });

  const totalDeliveryCost = RecalculateTotalDeliveryCost(quoteProduct.deliveryCosts);
  const displayOnly = RecalculateDisplayOnly(quoteProduct, marginConfiguration, defaultMargins);

  return {
    ...formikProduct,
    displayOnly,
    deliveryCosts: {
      ...FormikDeliveryCosts(deliveryCosts, resolveDefaultCurrencyCode({ plantProduct })),
      displayOnly: {
        totalDeliveryCost,
        adjustedDriveToJobTime: hasValue(deliveryCosts?.driveToJobTime)
          ? adjustDuration(
              deliveryCosts.driveToJobTime,
              deliveryCosts?.drivingTimeAdjustmentPercentage,
            ).toISO()
          : null,
        adjustedDriveToPlantTime: hasValue(deliveryCosts?.driveToPlantTime)
          ? adjustDuration(
              deliveryCosts.driveToPlantTime,
              deliveryCosts?.drivingTimeAdjustmentPercentage,
            ).toISO()
          : null,
      },
    },
  };
};

/**
 * Recalculate the displayOnly values for ready-mix products on
 * ProjectOrQuoteProductFormikType and its deliveryCosts, and set the new values
 * in the formikBag.
 */
export const setRecalculatedRMCosts = ({
  formikBag,
  deliveryCostInputOverrides = {},
  quoteProductOverrides = {},
  fieldPathPrefix,
  formikProduct,
  plantProduct,
  marginConfiguration,
  defaultMargins,
}: {
  formikBag: FormikContextType<SharedFormikType>;
  deliveryCostInputOverrides?: PartialDeep<PlantFormikType['deliveryCosts']>;
  quoteProductOverrides?: PartialDeep<QuoteProduct>;
  fieldPathPrefix: ProductFieldPathPrefix;
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;
  plantProduct: Product | undefined;
  marginConfiguration: Enums.MarginConfiguration;
  defaultMargins: TenantDefaultMargins;
}): setRecalculatedRMCostsReturn => {
  const recalculated = setRecalculatedRMCostsNoBag({
    deliveryCostInputOverrides,
    quoteProductOverrides,
    formikProduct,
    plantProduct,
    marginConfiguration,
    defaultMargins,
  });

  formikBag.setFieldValue(fieldPathPrefix, recalculated, false);

  return {
    totalDeliveryCost: recalculated.deliveryCosts.displayOnly?.totalDeliveryCost ?? '',
    displayOnly: recalculated.displayOnly,
  };
};

/* -------------------------------------------------------------------------- */
/* With-delivery (aggregate/asphalt) helpers                                  */
/* -------------------------------------------------------------------------- */
export const clearWithDeliveryFields = ({
  formikBag,
  fieldPathPrefix,
  formikProduct,
}: {
  formikBag: FormikContextType<SharedFormikType>;
  fieldPathPrefix: `${ProductSectionType}.${number}`;
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;
}): void => {
  if (
    formikProduct.deliveryType === Enums.ProductDeliveryType.Hauling ||
    formikProduct.deliveryType === Enums.ProductDeliveryType.InternalHauling
  ) {
    return;
  }
  // Using setFieldValue directly for nested paths that SetFormikValue doesn't fully support
  formikBag.setFieldValue(`${fieldPathPrefix}.truckingType`, { id: null, option: null }, false);
  formikBag.setFieldValue(`${fieldPathPrefix}.haulRate`, null, false);
  formikBag.setFieldValue(`${fieldPathPrefix}.haulCost`, null, false);
  formikBag.setFieldValue(`${fieldPathPrefix}.minimumHaulCharge`, null, false);
  formikBag.setFieldValue(`${fieldPathPrefix}.haulTaxable`, false, false);
  formikBag.setFieldValue(`${fieldPathPrefix}.additionalTruckingInfos`, [], false);
};

export const setRecalculatedWithDeliveryCostsNoBag = (
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>,
  formikTruckingInfo?: AdditionalTruckingInfoEditableFieldsFormikType,
): WithDeliveryDisplayOnlyType => {
  // if formikTruckingInfo is provided, use its values, otherwise use the formikProduct values.
  const sourceHaulRate = formikTruckingInfo ? formikTruckingInfo.haulRate : formikProduct.haulRate;
  const sourceHaulCost = formikTruckingInfo ? formikTruckingInfo.haulCost : formikProduct.haulCost;
  const sourceTaxRate = formikTruckingInfo ? formikTruckingInfo.taxRate : formikProduct.taxRate;
  const sourceHaulTaxable = formikTruckingInfo
    ? formikTruckingInfo.haulTaxable
    : formikProduct.haulTaxable;

  const haulRate = ((): Currency => {
    // If it is pick up, the haul rate is always 0.
    if (formikProduct.deliveryType === Enums.ProductDeliveryType.Pickup) {
      return Currency.zero();
    }

    // Otherwise, use the haul rate from the input field.
    return new Currency(sourceHaulRate ?? undefined);
  })();

  const isNonTaxable = formikProduct.product.nonTaxable ?? false;
  const taxRate = isNonTaxable ? 0 : Number(sourceTaxRate ?? 0) / 100;

  const unitPrice = new Currency(formikProduct.price);
  const subtotal = haulRate.add(unitPrice);
  const total = CalculateWithDeliveryTotalPrice({
    unitPrice,
    haulRate,
    taxRate,
    haulTaxable: sourceHaulTaxable,
  });

  const displaySubtotal = NullableCurrencyString({ cur: subtotal });
  const displayTotal = NullableCurrencyString({ cur: total });
  const haulMargin = NewQuoteProduct({
    haulCost: sourceHaulCost,
    haulRate: sourceHaulRate,
  }).haulMarginAsPercentage();

  return {
    withDeliverySubtotalPrice: displaySubtotal,
    withDeliveryTotalPrice: displayTotal,
    haulMargin,
  };
};

/**
 * Recalculate the "with delivery" (hauling) costs for an additional trucking info element and set the new values in the formikBag.
 */
export const setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo = ({
  formikBag,
  fieldPathPrefix,
  formikTruckingInfo,
  formikProduct,
}: {
  formikBag: FormikContextType<SharedFormikType>;
  fieldPathPrefix: AdditionalTruckingInfoFieldPathPrefix;
  formikTruckingInfo: AdditionalTruckingInfoEditableFieldsFormikType;
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;
}): void => {
  formikBag.setFieldValue(
    fieldPathPrefix,
    {
      ...formikTruckingInfo,
      withDeliveryDisplayOnly: setRecalculatedWithDeliveryCostsNoBag(
        formikProduct,
        formikTruckingInfo,
      ),
    },
    false,
  );
};

/**
 * Recalculate the "with delivery" (hauling) costs for a product and set the new values in the formikBag.
 */
export const setRecalculatedWithDeliveryCosts = ({
  formikBag,
  fieldPathPrefix,
  formikProduct,
}: {
  formikBag: FormikContextType<SharedFormikType>;
  fieldPathPrefix: ProductFieldPathPrefix;
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;
}): void => {
  formikBag.setFieldValue(
    fieldPathPrefix,
    {
      ...formikProduct,
      withDeliveryDisplayOnly: setRecalculatedWithDeliveryCostsNoBag(formikProduct),
    },
    false,
  );
};

type HandleTruckingTypeChangeParams = {
  formikBag: FormikContextType<SharedFormikType>;
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;
  defaults: TruckingTypeDefaults;
  // The formik values a lookup's onMatchChange sees still hold the previous type
  truckingType?: YupSchemaReferenceType;
} & (
  | { fieldPathPrefix: ProductFieldPathPrefix }
  | {
      fieldPathPrefix: AdditionalTruckingInfoFieldPathPrefix;
      formikTruckingInfo: AdditionalTruckingInfoEditableFieldsFormikType;
    }
);

export const handleTruckingTypeChange = (params: HandleTruckingTypeChangeParams): void => {
  const { defaults, fieldPathPrefix, formikProduct, formikBag, truckingType } = params;
  const formattedHaulRate = FormikNullableCurrency(defaults.haulRate);
  const formattedHaulCost = FormikNullableCurrency(defaults.haulCost);
  const minimumHaulCharge = defaults.minimumHaulCharge ?? null;
  const haulRatePath = `${fieldPathPrefix}.haulRate`;
  const haulCostPath = `${fieldPathPrefix}.haulCost`;
  const minimumHaulChargePath = `${fieldPathPrefix}.minimumHaulCharge`;

  formikBag.setFieldValue(haulRatePath, formattedHaulRate, false);
  formikBag.setFieldValue(haulCostPath, formattedHaulCost, false);
  formikBag.setFieldValue(minimumHaulChargePath, minimumHaulCharge, false);

  const updatedValues = {
    haulRate: formattedHaulRate,
    haulCost: formattedHaulCost,
    minimumHaulCharge: minimumHaulCharge,
    ...(hasValue(truckingType) && { truckingType }),
  };

  // If the trucking info is for an additional trucking info, update the additional trucking info.
  // Otherwise, update the product.
  if ('formikTruckingInfo' in params) {
    setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo({
      formikBag,
      fieldPathPrefix: params.fieldPathPrefix,
      formikTruckingInfo: {
        ...params.formikTruckingInfo,
        ...updatedValues,
      },
      formikProduct,
    });
  } else {
    setRecalculatedWithDeliveryCosts({
      formikBag,
      fieldPathPrefix: params.fieldPathPrefix,
      formikProduct: {
        ...formikProduct,
        ...updatedValues,
      },
    });
  }
};

/** Determine the default value of QuoteProduct.price for a Product that is newly added to the Quote. */
export const CalculateInitialPrice = ({
  plantProduct,
  projectProduct,
  deliveryCosts,
  defaultToSuggestedPrice = false,
  tierPrice = null,
  marginConfiguration = Enums.MarginConfiguration.Percentage,
  defaultMargins = EmptyTenantDefaultMargins,
}: {
  plantProduct: Product | undefined;
  projectProduct: ProjectProduct | undefined;
  deliveryCosts: DeliveryCost | undefined;
  defaultToSuggestedPrice?: boolean;
  tierPrice?: Currency | null;
  marginConfiguration?: Enums.MarginConfiguration;
  defaultMargins?: TenantDefaultMargins;
}): Currency => {
  const defaultCurrencyCode = resolveDefaultCurrencyCode({ plantProduct });

  // If no current product, clear the price
  if (plantProduct === undefined) {
    return NewCurrency({
      number: '',
      currency: defaultCurrencyCode,
    });
  }

  // Tier price takes highest priority
  if (tierPrice !== null) {
    return tierPrice;
  }

  // Prefer project product unit price if exists
  if (projectProduct?.price !== undefined) {
    return projectProduct.price;
  }

  if (defaultToSuggestedPrice) {
    const suggestedPrice = NewQuoteProduct({
      product: plantProduct,
      deliveryCosts:
        deliveryCosts ?? projectProduct?.deliveryCosts ?? plantProduct.plant.deliveryCosts,
    }).calculateSuggestedPrice(marginConfiguration, defaultMargins);
    if (suggestedPrice !== null) return suggestedPrice;
    if (plantProduct.listPrice !== null) return plantProduct.listPrice;
    return NewCurrency({ number: '', currency: defaultCurrencyCode });
  }

  if (plantProduct.listPrice !== null) return plantProduct.listPrice;
  const suggestedPrice = NewQuoteProduct({
    product: plantProduct,
    deliveryCosts:
      deliveryCosts ?? projectProduct?.deliveryCosts ?? plantProduct.plant.deliveryCosts,
  }).calculateSuggestedPrice(marginConfiguration, defaultMargins);
  if (suggestedPrice !== null) return suggestedPrice;
  return NewCurrency({ number: '', currency: defaultCurrencyCode });
};

// Blank form row
export const NewEmptySharedFormikProduct = ({
  sectionType,
  defaultIsMoneySwitchOn = false,
  plantProduct,
}: {
  sectionType: Enums.QuoteProductKind;
  defaultIsMoneySwitchOn?: boolean;
  plantProduct?: Product | null;
}): ProjectOrQuoteProductFormikType => ({
  product: {
    id: null,
    option: null,
    incompatibleMixQuantityUnits: false,
    allowIncompatibleMixQuantityUnits: false,
  },
  discounts: [],
  kind:
    sectionType === Enums.QuoteProductKind.Primary
      ? Enums.QuoteProductKind.Primary
      : Enums.QuoteProductKind.Additional,
  quantity: null,
  externalName: null,
  class: null,
  price: {
    number: '',
    currency: resolveDefaultCurrencyCode({ plantProduct }),
  },
  originalPrice: {
    number: '',
    currency: resolveDefaultCurrencyCode({ plantProduct }),
  },
  isMoneySwitchOn: defaultIsMoneySwitchOn,
  displayOnly: null,
  deliveryCosts: deliveryCostToFormik(
    buildInitialDeliveryCosts({
      deliveryCosts: DeliveryCost.zero(),
      drivingDistance: null,
      drivingTime: null,
    }),
    resolveDefaultCurrencyCode({ plantProduct }),
  ),
  usage: null,

  // Delivery Pricing-specific fields
  haulTaxable: false,
  deliveryType: null,
  haulRate: {
    number: '',
    currency: resolveDefaultCurrencyCode({ plantProduct }),
  },
  minimumHaulCharge: null,
  taxRate: null,
  truckingType: {
    id: null,
    option: null,
  },
  haulCost: {
    number: '',
    currency: resolveDefaultCurrencyCode({ plantProduct }),
  },
  additionalTruckingInfos: [],
  withDeliveryDisplayOnly: null,
});

// blank form row for additional trucking info
export const NewEmptyAdditionalTruckingInfoFormik = (
  plantProduct?: Product | null,
): AdditionalTruckingInfoFormikType => ({
  truckingType: {
    id: null,
    option: null,
  },
  haulRate: {
    number: '',
    currency: resolveDefaultCurrencyCode({ plantProduct }),
  },
  minimumHaulCharge: null,
  taxRate: null,
  haulTaxable: false,
  haulCost: {
    number: '',
    currency: resolveDefaultCurrencyCode({ plantProduct }),
  },
  withDeliveryDisplayOnly: null,
});

/* -------------------------------------------------------------------------- */
/* Action factory for per-row handlers (for UI layers to call directly)       */
/* -------------------------------------------------------------------------- */

type CreateActionsArgs = {
  formikBag: FormikContextType<SharedFormikType>;
  formikSectionType: ProductSectionType;
  sectionType: Enums.QuoteProductKind;
  project: Project | null | undefined;
  curProductsRef: MutableRefObject<Record<string, Product>>;
  projectProductsByProductID: Record<string, ProjectProduct>;
  marginConfiguration: Enums.MarginConfiguration;
  defaultMargins: TenantDefaultMargins;
  defaultToSuggestedPrice?: boolean;
  fixedPricingByCompany?: FixedPricingByCompany;
  plantId?: string;
};

export type PlantDistanceInfo = {
  plantDistance: string | null;
  plantDistanceUnit: Enums.Unit | null;
  plantDriveTime: string | null;
};
/**
 * Helper function to get plant distance and drive time from the correct location
 * based on the formikSectionType and project context.
 */
const getPlantDistanceAndDriveTime = (
  formikBag: FormikContextType<SharedFormikType>,
  formikSectionType: ProductSectionType,
  project: Project | null | undefined,
): PlantDistanceInfo => {
  // For plant-based sections (quotes and multi-location projects),
  // get the parent path by removing '.products' suffix
  if (formikSectionType.endsWith('.products')) {
    const plantPath = formikSectionType.replace(/\.products$/, '');
    const plantData = getIn(formikBag.values, plantPath);

    if (hasValue(plantData?.plantDistance) || hasValue(plantData?.plantDriveTime)) {
      return {
        plantDistance: plantData.plantDistance ?? null,
        plantDistanceUnit: plantData.plantDistanceUnit ?? null,
        plantDriveTime: plantData.plantDriveTime ?? null,
      };
    }
  }

  // Fallback for project context: use the project's values if available
  if (hasValue(project)) {
    return {
      plantDistance: project.plantDistance,
      plantDistanceUnit: project.plantDistanceUnit,
      plantDriveTime: project.plantDriveTime?.toISO() ?? null,
    };
  }

  return {
    plantDistance: null,
    plantDistanceUnit: null,
    plantDriveTime: null,
  };
};

export const updateFormikProduct = (
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>,
  {
    distanceInfo,
    newProjectProduct,
    newPlantProduct,
    marginConfiguration,
    defaultMargins,
    quantity = null,
    usage = null,
    defaultToSuggestedPrice = false,
    fixedPricingByCompany = undefined,
    plantId = undefined,
    isQuoteLine,
  }: {
    distanceInfo: PlantDistanceInfo;
    newProjectProduct?: ProjectProduct;
    newPlantProduct: Product;
    marginConfiguration: Enums.MarginConfiguration;
    defaultMargins: TenantDefaultMargins;
    quantity: string | null;
    usage: string | null;
    defaultToSuggestedPrice?: boolean;
    fixedPricingByCompany?: FixedPricingByCompany;
    plantId?: string | null;
    isQuoteLine: boolean;
  },
) => {
  const sourceProduct = newProjectProduct?.product ?? newPlantProduct;
  const product = {
    ...FormikProductReference(sourceProduct),
    nonTaxable: isQuoteLine && sourceProduct.nonTaxable,
  };

  formikProduct = { ...formikProduct, product };

  const newDeliveryCost = newProductDeliveryCost({
    plantProduct: newPlantProduct,
    projectProduct: newProjectProduct,
  });

  const { plantDistance, plantDriveTime } = distanceInfo;
  const initialDeliveryCosts = ((): DeliveryCost => {
    return buildInitialDeliveryCosts({
      deliveryCosts: newDeliveryCost,
      drivingTime: plantDriveTime,
      drivingDistance: plantDistance ?? '0',
    });
  })();
  const initialFormikDeliveryCosts = deliveryCostToFormik(
    initialDeliveryCosts,
    resolveDefaultCurrencyCode({ plantProduct: newPlantProduct }),
  );
  formikProduct = { ...formikProduct, deliveryCosts: initialFormikDeliveryCosts };

  const basePrice = CalculateInitialPrice({
    plantProduct: newPlantProduct,
    projectProduct: newProjectProduct,
    deliveryCosts: initialDeliveryCosts,
    defaultToSuggestedPrice,
    tierPrice: null,
    marginConfiguration: marginConfiguration,
    defaultMargins: defaultMargins,
  });

  const [tierAdjustedPrice, appliedTierType] = calculateTierAdjustment(
    fixedPricingByCompany,
    basePrice,
    plantId,
    newPlantProduct.id,
  );
  const rawPrice =
    (appliedTierType !== AppliedTierType.None ? (tierAdjustedPrice ?? null) : null) ?? basePrice;
  const rowDiscounts = formikProduct.discounts ?? [];
  const rawGrossPrice = parseFloat(cleanedDecimal(rawPrice.number) || '0');
  const discounts = rescaledToGrossPrice(
    rowDiscounts,
    parseFloat(cleanedDecimal(formikProduct.price.number || '0')) || 0,
    rawGrossPrice,
  );
  const netPriceNumber = settledNetPrice(discounts, rawGrossPrice).toFixed(4);
  const priceNumber =
    rowDiscounts.length === 0
      ? formatDecimalString(rawPrice.number) || rawPrice.number
      : formatDecimalString(netPriceNumber) || netPriceNumber;
  const price = FormikCurrency(new Currency({ number: priceNumber, currency: rawPrice.currency }));

  formikProduct = {
    ...formikProduct,
    ...(rowDiscounts.length > 0 ? { discounts: pendingDiscounts(discounts) } : {}),
    price,
    originalPrice: FormikCurrency(basePrice),
    quantity,
    externalName: newPlantProduct.externalName(),
    usage,
  };

  formikProduct = setRecalculatedRMCostsNoBag({
    formikProduct: formikProduct,
    plantProduct: newPlantProduct,
    marginConfiguration,
    defaultMargins,
    deliveryCostInputOverrides: initialFormikDeliveryCosts,
    quoteProductOverrides: {
      price,
      product: newPlantProduct,
      deliveryCosts: initialDeliveryCosts,
    },
  });

  const pricedFormikProduct = { ...formikProduct, price };
  return {
    ...formikProduct,
    withDeliveryDisplayOnly: setRecalculatedWithDeliveryCostsNoBag(pricedFormikProduct),
    additionalTruckingInfos: formikProduct.additionalTruckingInfos.map((info) => ({
      ...info,
      withDeliveryDisplayOnly: setRecalculatedWithDeliveryCostsNoBag(pricedFormikProduct, info),
    })),
  };
};

export const createProductSectionActions = ({
  formikBag,
  formikSectionType,
  sectionType,
  project,
  curProductsRef,
  projectProductsByProductID,
  marginConfiguration,
  defaultMargins,
  defaultToSuggestedPrice = false,
  fixedPricingByCompany,
  plantId,
}: CreateActionsArgs): {
  actionsForIndex: (
    index: number,
    plantProduct: Product | null,
  ) => {
    onProductSelected: (productID: string | null, productList?: { items: Product[] }) => void;
    onUnitPriceChange: (
      unitPriceNumber: string,
      extraFields?: Partial<ProjectOrQuoteProductFormikType>,
    ) => void;
    onGrossPriceChange: (
      grossPriceNumber: string,
      onExceeding?: ExceedingDiscounts,
      rematch?: boolean,
    ) => void;
    onDeliveryCostOverrideChange: (numberOrEmpty: string) => void;
    onPourTimeChange: (pourTimeISO: string | '') => void;
    onWaitTimeChange: (waitTimeISO: string | '') => void;
    onLoadSizeChange: (loadSizeNumber: string) => void;
    onDeliveryTypeChange: (deliveryType: Enums.ProductDeliveryType | null) => void;
    onHaulRateChange: (numberStr: string) => void;
    onMinimumHaulChargeChange: (value: number | null) => void;
    onHaulTaxableChange: (haulTaxable: boolean) => void;
    onTaxRateChange: (taxRateStr: string) => void;
    onHaulCostChange: (numberStr: string) => void;
    onTruckingTypeChange: (
      defaults: TruckingTypeDefaults,
      truckingType?: YupSchemaReferenceType,
    ) => void;
  };
  actionsForAdditionalTruckingInfo: (
    productIndex: number,
    additionalTruckingInfoIndex: number,
    plantProduct: Product | null,
  ) => TruckingTypeActions;
  reorder: (sourceIndex: number, destinationIndex: number) => void;
  addRow: (defaultIsMoneySwitchOn: boolean) => void;
  removeRow: (index: number) => void;
  addTruckingInfoRow: (productIndex: number, plantProduct: Product | null) => void;
} => {
  const actionsForAdditionalTruckingInfo = (
    productIndex: number,
    additionalTruckingInfoIndex: number,
    plantProduct: Product | null,
  ): TruckingTypeActions => {
    const productFieldPathPrefix = `${formikSectionType}.${productIndex}`;
    const additionalTruckingInfoFieldPathPrefix =
      `${productFieldPathPrefix}.additionalTruckingInfos.${additionalTruckingInfoIndex}` as AdditionalTruckingInfoFieldPathPrefix;

    const formikProduct = formikBag.getFieldMeta<
      Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>
    >(productFieldPathPrefix as any).value;
    const formikTruckingInfo = formikProduct.additionalTruckingInfos?.[additionalTruckingInfoIndex];
    return {
      onHaulRateChange: (numberStr: string): void => {
        setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo({
          formikBag,
          fieldPathPrefix: additionalTruckingInfoFieldPathPrefix,
          formikTruckingInfo: {
            ...formikTruckingInfo,
            haulRate: NewCurrency({
              number: numberStr,
              currency: resolveDefaultCurrencyCode({ plantProduct }),
            }),
          },
          formikProduct,
        });
      },
      onMinimumHaulChargeChange: (value: number | null): void => {
        setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo({
          formikBag,
          fieldPathPrefix: additionalTruckingInfoFieldPathPrefix,
          formikTruckingInfo: {
            ...formikTruckingInfo,
            minimumHaulCharge: value,
          },
          formikProduct,
        });
      },
      onHaulTaxableChange: (haulTaxable: boolean): void => {
        setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo({
          formikBag,
          fieldPathPrefix: additionalTruckingInfoFieldPathPrefix,
          formikTruckingInfo: {
            ...formikTruckingInfo,
            haulTaxable: haulTaxable,
          },
          formikProduct,
        });
      },
      onTaxRateChange: (taxRateStr: string): void => {
        setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo({
          formikBag,
          fieldPathPrefix: additionalTruckingInfoFieldPathPrefix,
          formikTruckingInfo: {
            ...formikTruckingInfo,
            taxRate: taxRateStr,
          },
          formikProduct,
        });
      },
      onHaulCostChange: (numberStr: string): void => {
        setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo({
          formikBag,
          fieldPathPrefix: additionalTruckingInfoFieldPathPrefix,
          formikTruckingInfo: {
            ...formikTruckingInfo,
            haulCost: NewCurrency({
              number: numberStr,
              currency: resolveDefaultCurrencyCode({ plantProduct }),
            }),
          },
          formikProduct,
        });
      },
      onTruckingTypeChange: (
        defaults: TruckingTypeDefaults,
        truckingType?: YupSchemaReferenceType,
      ): void => {
        handleTruckingTypeChange({
          defaults,
          truckingType,
          fieldPathPrefix: additionalTruckingInfoFieldPathPrefix,
          formikProduct,
          formikBag,
          formikTruckingInfo,
        });
      },
    };
  };

  const addTruckingInfoRow = (productIndex: number, plantProduct: Product | null): void => {
    const productFieldPathPrefix = `${formikSectionType}.${productIndex}`;
    const formikProduct = formikBag.getFieldMeta<
      Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>
    >(productFieldPathPrefix as any).value;

    const newAdditionalTruckingInfo = NewEmptyAdditionalTruckingInfoFormik(plantProduct);

    formikBag.setFieldValue(`${productFieldPathPrefix}.additionalTruckingInfos`, [
      ...formikProduct.additionalTruckingInfos,
      {
        ...newAdditionalTruckingInfo,
        withDeliveryDisplayOnly: setRecalculatedWithDeliveryCostsNoBag(
          formikProduct,
          newAdditionalTruckingInfo,
        ),
      },
    ]);
  };

  const actionsForIndex = (
    index: number,
    plantProduct: Product | null,
  ): {
    onProductSelected: (productID: string | null, productList?: { items: Product[] }) => void;
    onUnitPriceChange: (
      unitPriceNumber: string,
      extraFields?: Partial<ProjectOrQuoteProductFormikType>,
    ) => void;
    onGrossPriceChange: (
      grossPriceNumber: string,
      onExceeding?: ExceedingDiscounts,
      rematch?: boolean,
    ) => void;
    onDeliveryCostOverrideChange: (numberOrEmpty: string) => void;
    onPourTimeChange: (pourTimeISO: string | '') => void;
    onWaitTimeChange: (waitTimeISO: string | '') => void;
    onLoadSizeChange: (loadSizeNumber: string) => void;
    onDeliveryTypeChange: (deliveryType: Enums.ProductDeliveryType | null) => void;
    onHaulRateChange: (numberStr: string) => void;
    onMinimumHaulChargeChange: (value: number | null) => void;
    onHaulTaxableChange: (haulTaxable: boolean) => void;
    onTaxRateChange: (taxRateStr: string) => void;
    onHaulCostChange: (numberStr: string) => void;
    onTruckingTypeChange: (
      defaults: TruckingTypeDefaults,
      truckingType?: YupSchemaReferenceType,
    ) => void;
  } => {
    const fieldPathPrefix = `${formikSectionType}.${index}` as ProductFieldPathPrefix;
    const formikProduct = formikBag.getFieldMeta<
      Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>
    >(fieldPathPrefix as any).value;
    const defaultCurrencyCode = resolveDefaultCurrencyCode({ plantProduct });
    return {
      /** Called when the Product lookup changes. */
      onProductSelected: (productID: string | null, productList?: { items: Product[] }): void => {
        if (productID === null) {
          // Using setFieldValue directly for nested paths
          formikBag.setFieldValue(`${formikSectionType}.${index}`, {
            ...NewEmptySharedFormikProduct({
              sectionType,
              plantProduct,
            }),
            isMoneySwitchOn: formikProduct.isMoneySwitchOn,
          });
          return;
        }

        const newPlantProduct =
          (productList?.items ?? []).find((p) => p.id === productID) ?? Product.zero();
        const newProjectProduct = projectProductsByProductID[productID];

        const distanceInfo = getPlantDistanceAndDriveTime(formikBag, formikSectionType, project);

        formikBag.setFieldValue(
          fieldPathPrefix,
          updateFormikProduct(formikProduct, {
            defaultMargins,
            newPlantProduct,
            newProjectProduct,
            marginConfiguration,
            distanceInfo,
            quantity: null,
            usage:
              sectionType === Enums.QuoteProductKind.Primary
                ? (newProjectProduct?.usage ?? null)
                : null,
            defaultToSuggestedPrice: [
              Enums.ProductCategory.ReadyMix,
              Enums.ProductCategory.Asphalt,
              Enums.ProductCategory.Aggregate,
            ].includes(newPlantProduct.category)
              ? defaultToSuggestedPrice
              : false,
            fixedPricingByCompany,
            plantId,
            isQuoteLine: !formikSectionType.startsWith('productsByPlants.'),
          }),
        );

        // Cache by product ID for stable lookups across row/index changes.
        curProductsRef.current = {
          ...curProductsRef.current,
          [newPlantProduct.id]: newPlantProduct,
        };
      },

      onUnitPriceChange: (
        unitPriceNumber: string,
        extraFields?: Partial<ProjectOrQuoteProductFormikType>,
      ): void => {
        // Apply both recalculations in sequence and write once so the second doesn't clobber the first
        const price = NewCurrency({ number: unitPriceNumber, currency: defaultCurrencyCode });
        const rowDiscounts = formikProduct.discounts ?? [];
        const repricedDiscounts =
          extraFields?.discounts === undefined && rowDiscounts.length > 0
            ? {
                discounts: rescaledToNetPrice(
                  rowDiscounts,
                  parseFloat(cleanedDecimal(formikProduct.price.number || '0')) || 0,
                  parseFloat(cleanedDecimal(unitPriceNumber) || '0') || 0,
                  savedPercentSharesOfRows(
                    (getIn(formikBag.initialValues, formikSectionType) as
                      | ProjectOrQuoteProductFormikType[]
                      | undefined) ?? [],
                  ),
                ),
              }
            : {};

        const recalculatedRM = setRecalculatedRMCostsNoBag({
          formikProduct: {
            ...formikProduct,
            price,
          },
          plantProduct: plantProduct ?? undefined,
          marginConfiguration,
          defaultMargins,
          quoteProductOverrides: { price },
        });

        const recalculatedWithDeliveryCosts = setRecalculatedWithDeliveryCostsNoBag({
          ...recalculatedRM,
          price,
        });
        formikBag.setFieldValue(
          fieldPathPrefix,
          {
            ...recalculatedRM,
            withDeliveryDisplayOnly: recalculatedWithDeliveryCosts,
            ...repricedDiscounts,
            ...extraFields,
          },
          false,
        );

        // Recalculate the delivery costs for each additional trucking info.
        if (formikProduct.additionalTruckingInfos.length > 0) {
          formikProduct.additionalTruckingInfos.forEach(
            (additionalTruckingInfo, additionalTruckingInfoIndex) => {
              setRecalculatedWithDeliveryCostsForAdditionalTruckingInfo({
                formikBag,
                fieldPathPrefix: `${fieldPathPrefix}.additionalTruckingInfos.${additionalTruckingInfoIndex}`,
                formikTruckingInfo: additionalTruckingInfo,
                formikProduct: {
                  ...formikProduct,
                  price: { number: unitPriceNumber, currency: defaultCurrencyCode },
                },
              });
            },
          );
        }
      },

      // For callers that hold a gross price (tier, list price): the row's discounts are rescaled
      // to it and the net is written, so a discounted line never takes the gross as its net.
      onGrossPriceChange: (
        grossPriceNumber: string,
        onExceeding: ExceedingDiscounts = ExceedingDiscounts.Keep,
        rematch = false,
      ): void => {
        const rowDiscounts = formikProduct.discounts ?? [];
        if (rowDiscounts.length === 0) {
          actionsForIndex(index, plantProduct).onUnitPriceChange(grossPriceNumber);
          return;
        }
        const repriced = repricedAtGross(
          rowDiscounts,
          parseFloat(cleanedDecimal(formikProduct.price.number || '0')) || 0,
          parseFloat(cleanedDecimal(grossPriceNumber) || '0') || 0,
          onExceeding,
        );
        if (repriced === null) {
          return;
        }
        const netPriceNumber = repriced.netPrice.toFixed(4);
        actionsForIndex(index, plantProduct).onUnitPriceChange(
          formatDecimalString(netPriceNumber) || netPriceNumber,
          { discounts: rematch ? pendingDiscounts(repriced.discounts) : repriced.discounts },
        );
      },

      onDeliveryCostOverrideChange: (numberOrEmpty: string): void => {
        if (plantProduct === null) return; // nothing to do without product

        const cascaded = CascadedDeliveryCost({
          formikProduct,
          projectProduct: projectProductsByProductID[plantProduct.id],
          plantProduct,
        });

        const formikDC =
          cascaded.shouldUpdate === false
            ? cascaded.formikDeliveryCost
            : FormikDeliveryCosts(
                cascaded.deliveryCost ?? undefined,
                resolveDefaultCurrencyCode({ plantProduct }),
              );

        const amountNumber = numberOrEmpty?.trim() ?? '';
        const override =
          amountNumber === ''
            ? null
            : {
                amount: {
                  number: amountNumber,
                  currency: resolveDefaultCurrencyCode({ plantProduct }),
                },
                unit:
                  formikProduct.deliveryCosts.deliveryCostOverride?.unit ??
                  FormikDeliveryCosts(undefined).deliveryCostOverride.unit,
              };

        const { totalDeliveryCost } = setRecalculatedRMCosts({
          formikBag,
          formikProduct,
          fieldPathPrefix,
          plantProduct,
          marginConfiguration,
          defaultMargins,
          deliveryCostInputOverrides: { ...formikDC, deliveryCostOverride: override ?? undefined },
        });

        if (cascaded.shouldUpdate) {
          const { plantDistance, plantDriveTime } = getPlantDistanceAndDriveTime(
            formikBag,
            formikSectionType,
            project,
          );
          const base = buildInitialDeliveryCosts({
            deliveryCosts: cascaded.deliveryCost,
            drivingTime: plantDriveTime,
            drivingDistance: plantDistance ?? '0',
          });

          // Using setFieldValue directly for nested paths
          formikBag.setFieldValue(`${fieldPathPrefix}.deliveryCosts`, {
            ...FormikDeliveryCosts(base, resolveDefaultCurrencyCode({ plantProduct })),
            deliveryCostOverride: override,
            displayOnly: {
              totalDeliveryCost,
              adjustedDriveToJobTime: hasValue(cascaded.deliveryCost?.driveToJobTime)
                ? adjustDuration(
                    cascaded.deliveryCost.driveToJobTime,
                    cascaded.deliveryCost?.drivingTimeAdjustmentPercentage,
                  ).toISO()
                : null,
              adjustedDriveToPlantTime: hasValue(cascaded.deliveryCost?.driveToPlantTime)
                ? adjustDuration(
                    cascaded.deliveryCost.driveToPlantTime,
                    cascaded.deliveryCost?.drivingTimeAdjustmentPercentage,
                  ).toISO()
                : null,
            },
          });
        }
      },

      onPourTimeChange: (pourTimeISO: string | ''): void => {
        setRecalculatedRMCosts({
          formikBag,
          formikProduct,
          fieldPathPrefix,
          plantProduct: plantProduct ?? undefined,
          marginConfiguration,
          defaultMargins,
          deliveryCostInputOverrides: { pourTime: pourTimeISO === '' ? '' : pourTimeISO },
        });
      },

      onWaitTimeChange: (waitTimeISO: string | ''): void => {
        setRecalculatedRMCosts({
          formikBag,
          formikProduct,
          fieldPathPrefix,
          plantProduct: plantProduct ?? undefined,
          marginConfiguration,
          defaultMargins,
          deliveryCostInputOverrides: { waitTime: waitTimeISO === '' ? '' : waitTimeISO },
        });
      },

      onLoadSizeChange: (loadSizeNumber: string): void => {
        setRecalculatedRMCosts({
          formikBag,
          formikProduct,
          fieldPathPrefix,
          plantProduct: plantProduct ?? undefined,
          marginConfiguration,
          defaultMargins,
          deliveryCostInputOverrides: { loadSize: loadSizeNumber },
        });
      },

      onDeliveryTypeChange: (deliveryType: Enums.ProductDeliveryType | null): void => {
        const retargeted = {
          ...formikProduct,
          deliveryType,
          ...(hasValue(formikProduct.discounts) && formikProduct.discounts.length > 0
            ? { discounts: pendingDiscounts(formikProduct.discounts) }
            : {}),
        };
        setRecalculatedWithDeliveryCosts({
          formikBag,
          fieldPathPrefix,
          formikProduct: retargeted,
        });
        clearWithDeliveryFields({
          formikBag,
          fieldPathPrefix,
          formikProduct: retargeted,
        });
      },

      onHaulRateChange: (numberStr: string): void => {
        setRecalculatedWithDeliveryCosts({
          formikBag,
          fieldPathPrefix,
          formikProduct: {
            ...formikProduct,
            haulRate: NewCurrency({
              number: numberStr,
              currency: resolveDefaultCurrencyCode({ plantProduct }),
            }),
          },
        });
      },

      onMinimumHaulChargeChange: (value: number | null): void => {
        setRecalculatedWithDeliveryCosts({
          formikBag,
          fieldPathPrefix,
          formikProduct: { ...formikProduct, minimumHaulCharge: value },
        });
      },

      onHaulTaxableChange: (haulTaxable: boolean): void => {
        setRecalculatedWithDeliveryCosts({
          formikBag,
          fieldPathPrefix,
          formikProduct: { ...formikProduct, haulTaxable, taxRate: formikProduct.taxRate },
        });
      },

      onTaxRateChange: (taxRateStr: string): void => {
        setRecalculatedWithDeliveryCosts({
          formikBag,
          fieldPathPrefix,
          formikProduct: { ...formikProduct, taxRate: taxRateStr },
        });
      },

      onHaulCostChange: (numberStr: string): void => {
        setRecalculatedWithDeliveryCosts({
          formikBag,
          fieldPathPrefix,
          formikProduct: {
            ...formikProduct,
            haulCost: NewCurrency({
              number: numberStr,
              currency: resolveDefaultCurrencyCode({ plantProduct }),
            }),
          },
        });
      },

      /** Normalize trucking type: if cleared -> null; else `{id, option}` */
      onTruckingTypeChange: (
        defaults: TruckingTypeDefaults,
        truckingType?: YupSchemaReferenceType,
      ): void => {
        handleTruckingTypeChange({
          defaults,
          truckingType,
          fieldPathPrefix,
          formikProduct,
          formikBag,
        });
      },
    };
  };

  /** Reorder products + sync errors/touched (no useEffect). */
  const reorder = (sourceIndex: number, destinationIndex: number): void => {
    const items = Array.from(
      formikBag.getFieldMeta<Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[]>(
        formikSectionType,
      ).value,
    );
    const [moved] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, moved);
    formikBag.setFieldValue(formikSectionType, items, false);

    const sectionErrors =
      (getIn(formikBag.errors, formikSectionType as string) as any[] | undefined) ?? [];
    const sectionTouched =
      (getIn(formikBag.touched, formikSectionType as string) as any[] | undefined) ?? [];

    if (sectionErrors.length > 0) {
      const errs = Array.from(sectionErrors);
      const [movedErr] = errs.splice(sourceIndex, 1);
      errs.splice(destinationIndex, 0, movedErr);
      formikBag.setErrors({ ...formikBag.errors, [formikSectionType]: errs });
    }
    if (sectionTouched.length > 0) {
      const tch = Array.from(sectionTouched);
      const [movedTch] = tch.splice(sourceIndex, 1);
      tch.splice(destinationIndex, 0, movedTch);
      formikBag.setTouched({ ...formikBag.touched, [formikSectionType]: tch });
    }
  };

  /** Push a new blank product row. */
  const addRow = (defaultIsMoneySwitchOn: boolean): void => {
    const newProduct = NewEmptySharedFormikProduct({
      sectionType,
      defaultIsMoneySwitchOn,
    });
    const existing =
      formikBag.getFieldMeta<Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[]>(
        formikSectionType,
      ).value ?? [];
    formikBag.setFieldValue(formikSectionType, [...existing, newProduct], false);
  };

  /** Remove a row. */
  const removeRow = (index: number): void => {
    const items = Array.from(
      formikBag.getFieldMeta<Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[]>(
        formikSectionType,
      ).value,
    );
    items.splice(index, 1);
    formikBag.setFieldValue(formikSectionType, items, false);
  };

  return {
    actionsForIndex,
    actionsForAdditionalTruckingInfo,
    reorder,
    addRow,
    removeRow,
    addTruckingInfoRow,
  };
};
